import { useEffect, useState } from "react";
import { EntityCollection, ReadDatabase, SemanticIndexRequest, OasisIndexRequest } from "../../api/OasisBackendApi";
import { useOasisBackend } from "../../hooks/useOasisBackend";
import EntityUploader from "./EntityUploader";
import CodeBlock from "./CodeBlock";
import { toast, ToastContainer } from "react-toastify";

const GettingStarted = ({database}: {database: ReadDatabase}) => {
    const db = useOasisBackend();
    const [entityCollection, setEntityCollection] = useState<EntityCollection | null>(null);
    const [semanticEnabled, setSemanticEnabled] = useState(database.semantic_enabled);
    const [oasisEnabled, setOasisEnabled] = useState(database.oasis_enabled);
    const [semanticIndexRequests, setSemanticIndexRequests] = useState<SemanticIndexRequest[]>([]);
    const [oasisIndexRequests, setOasisIndexRequests] = useState<OasisIndexRequest[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Function to parse pipeline status and return status information
    const parsePipelineStatus = (pipelineStatus: any) => {
        if (!pipelineStatus || typeof pipelineStatus !== 'object') {
            return {
                pending: 0,
                running: 0,
                completed: 0,
                failed: 0,
                total: 0,
                completionPercentage: 0,
                statusText: "Initializing..."
            };
        }

        const statuses = Object.values(pipelineStatus);
        const total = statuses.length;
        const pending = statuses.filter((status: any) => status === 'PENDING').length;
        const running = statuses.filter((status: any) => status === 'RUNNING').length;
        const completed = statuses.filter((status: any) => status === 'COMPLETED').length;
        const failed = statuses.filter((status: any) => status === 'FAILED').length;
        const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        let statusText = "";
        if (failed > 0) {
            statusText = `Failed (${failed} failed)`;
        } else if (completed === total && total > 0) {
            setOasisEnabled(true);
            statusText = "Completed";
        } else if (running > 0) {
            statusText = `Running (${completionPercentage}% complete)`;
        } else if (pending > 0) {
            statusText = `Pending (${completionPercentage}% complete)`;
        } else {
            statusText = `Processing (${completionPercentage}% complete)`;
        }

        return {
            pending,
            running,
            completed,
            failed,
            total,
            completionPercentage,
            statusText
        };
    };

    const fetchEntityCollection = async () => {
        const response = await db.endpoints.api.apiDatabaseEntityCollectionRetrieve(database.id);
        setEntityCollection(response.data);
    }

    const indexEntities = async (index_type: string) => {
        const response = await db.endpoints.api.apiDatabaseIndexEntityCollectionCreate(database.id, {index_type: index_type as "semantic" | "oasis"});
        if (response.status === 201) {
            toast.success("Entities indexing processing. This will take a while. We will email you when it's done.");
            // Start polling immediately after initiating a new request
            fetchIndexRequests();
        } else {
            toast.error("Failed to index entities");
        }
    }

    const fetchIndexRequests = async () => {
        if (!entityCollection) return;
        try {
            const response = await db.endpoints.api.apiEntityCollectionIndexRequestsRetrieve(entityCollection.id);
            setSemanticIndexRequests(response.data.semantic_index_requests);
            setOasisIndexRequests(response.data.oasis_index_requests);
        } catch (error) {
            console.error("Error fetching index requests:", error);
            toast.error("Failed to fetch index requests");
        } finally {
            setIsLoading(false);
        }
    }
    
    // Check if there are any semantic requests not at failed or completed
    const hasActiveSemanticRequests = () => {
        const hasActive = semanticIndexRequests.some(request =>
            request.status !== "failed" && request.status !== "completed"
        );
        semanticIndexRequests.some(request => {
            if (request.status === "completed") {
                setSemanticEnabled(true);
            }
        });
        return hasActive;
    };

    // Check if there are any oasis requests not at failed or completed
    const hasActiveOasisRequests = () => {
        const hasActive = oasisIndexRequests.some(request =>
            request.status !== "failed" && request.status !== "completed"
        );
        oasisIndexRequests.some(request => {
            if (request.status === "completed") {
                setOasisEnabled(true);
            }
        });
        return hasActive;
    };

    const hasFailedOasisRequests = () => {
        return oasisIndexRequests.some(request => request.status === "failed");
    }

    const hasFailedSemanticRequests = () => {
        return semanticIndexRequests.some(request => request.status === "failed");
    }


    useEffect(() => {
        fetchEntityCollection();
    }, []);

    useEffect(() => {
        if (entityCollection) {
            fetchIndexRequests();
        }
    }, [entityCollection]);

    useEffect(() => {
        const allRequests = [...semanticIndexRequests, ...oasisIndexRequests];
        const hasActive = allRequests.some(request =>
            request.status !== "failed" && request.status !== "completed"
        );

        if (!hasActive) return;

        const pollInterval = setInterval(async () => {
            // Store previous request states to detect completion
            const previousRequests = [...semanticIndexRequests, ...oasisIndexRequests];
            
            await fetchIndexRequests();
            
            // Check if any request went from active to completed
            const currentRequests = [...semanticIndexRequests, ...oasisIndexRequests];
            const hadActiveRequest = previousRequests.some(request =>
                request.status !== "failed" && request.status !== "completed"
            );
            const hasCompletedRequest = currentRequests.some(request =>
                request.status === "completed"
            );
            
            // If we had active requests and now have completed ones, reload entity collection
            if (hasCompletedRequest) {
                await fetchEntityCollection();
                
                // Update enabled states based on completed requests
                const hasCompletedSemantic = semanticIndexRequests.some(request => request.status === "completed");
                const hasCompletedOasis = oasisIndexRequests.some(request => request.status === "completed");
                
                if (hasCompletedSemantic) {
                    setSemanticEnabled(true);
                }
                
                if (hasCompletedOasis) {
                    setOasisEnabled(true);
                }
            }
        }, 2000); // Poll every 2 seconds

        return () => clearInterval(pollInterval);
    }, [semanticIndexRequests, oasisIndexRequests]);

    if (!entityCollection) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="text-gray-300 text-lg">Loading...</span>
                </div>
            </div>
        )
    }

    // If no entities, show upload section
    if (entityCollection.entity_count === 0) {
        return (
            <div className="min-h-screen bg-gray-900 text-gray-100 p-4 sm:p-8">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-6 sm:mb-8">
                        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">No Entities Found</h2>
                        <p className="text-gray-400 text-base sm:text-lg">Upload your first entities to get started with intelligent search</p>
                    </div>
                    <div className="bg-gray-800 rounded-xl p-4 sm:p-8 border border-gray-700">
                        <div className="mb-6 text-center">
                            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                                Please upload a CSV file with your entities, where one row is one entity.
                            </p>
                        </div>
                        <EntityUploader DatabaseId={database.id} setEntityCollection={setEntityCollection} />
                    </div>
                </div>
            </div>
        );
    }

    // Show loading screen if index requests haven't been loaded yet
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-900 text-gray-100 p-4 sm:p-8">
                <div className="max-w-6xl mx-auto">
                    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mr-3"></div>
                            <span className="text-white text-lg">Loading index requests...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-gray-100 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
                {/* Header Section */}
                <div className="mb-6 sm:mb-8">
                    <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-4 sm:p-6 border border-gray-700">
                        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">Search Engine Setup</h1>
                        <p className="text-gray-300 text-base sm:text-lg">Configure your intelligent search capabilities</p>
                    </div>
                </div>

                {/* Entity Status */}
                <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700 mb-6 sm:mb-8">
                    <div className="flex items-center space-x-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <h2 className="text-lg sm:text-xl font-semibold text-white">Entities Ready</h2>
                    </div>
                    <p className="text-gray-400 mt-2">You have {entityCollection.entity_count} entities in your database</p>
                </div>

                {/* Search Options Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                    {/* Semantic Search Section */}
                    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 lg:p-8 border border-gray-700 hover:border-gray-600 transition-colors">
                        <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <h2 className="text-xl sm:text-2xl font-bold text-white">Semantic Search</h2>
                        </div>
                        
                        <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                            Traditional semantic search uses advanced AI to understand the meaning and context of your queries, 
                            finding the most relevant results based on semantic similarity.
                        </p>
                        
                        {semanticEnabled ? (
                            <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4 sm:p-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <h3 className="font-semibold text-green-400">Ready to Use</h3>
                                </div>
                                <CodeBlock databaseId={database.id} searchType="semantic" />
                            </div>
                        ) : (
                            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-4 sm:p-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                    <h3 className="font-semibold text-yellow-400">Not Enabled</h3>
                                </div>
                                <p className="text-yellow-200 mb-4 text-sm sm:text-base">Index your entities to enable semantic search capabilities.</p>
                                <button 
                                    disabled={hasActiveSemanticRequests()}
                                    className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 ${
                                        hasActiveSemanticRequests() 
                                            ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                                            : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                                    }`}
                                    onClick={() => indexEntities("semantic")}
                                >
                                    Enable Semantic Search
                                </button>
                                {(hasActiveSemanticRequests()) && (
                                    <p className="text-yellow-400 text-sm mt-2">
                                        Index buttons are disabled while requests are processing
                                    </p>
                                )}
                                {hasActiveSemanticRequests() || hasFailedSemanticRequests() ? (
                                    <div className="mt-4 space-y-3">
                                        {semanticIndexRequests.map((request) => {
                                            const isFailed = request.status === 'failed';
                                            const isCompleted = request.status === 'completed';
                                            const isRunning = request.status === 'running';
                                            const isPending = request.status === 'pending';
                                            
                                            let statusText = "";
                                            if (isFailed) {
                                                statusText = "Failed";
                                            } else if (isCompleted) {
                                                statusText = "Completed";
                                            } else if (isRunning) {
                                                statusText = "Running";
                                            } else if (isPending) {
                                                statusText = "Pending";
                                            } else {
                                                statusText = "Processing";
                                            }
                                            
                                            return (
                                                <div key={request.id} className={`flex items-center space-x-3 ${isFailed ? 'text-red-400' : 'text-blue-400'}`}>
                                                    {isFailed ? (
                                                        <div className="w-4 h-4 text-red-400">
                                                            <svg fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                                                    )}
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium">{statusText}</div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : <></>}
                            </div>
                        )}
                    </div>

                    {/* Oasis Search Section */}
                    <div className="bg-gray-800 rounded-xl p-4 sm:p-6 lg:p-8 border border-gray-700 hover:border-gray-600 transition-colors">
                        <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                            </div>
                            <h2 className="text-xl sm:text-2xl font-bold text-white">Oasis Search</h2>
                        </div>
                        
                        <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                            Oasis search leverages interpretable ontological embeddings to provide explainable and 
                            contextually aware search results with deep context understanding.
                        </p>
                        
                        {oasisEnabled ? (
                            <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4 sm:p-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    <h3 className="font-semibold text-green-400">Ready to Use</h3>
                                </div>
                                <CodeBlock databaseId={database.id} searchType="oasis" />
                            </div>
                        ) : (
                            <div className="bg-yellow-900/30 border border-yellow-500/30 rounded-lg p-4 sm:p-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                    <h3 className="font-semibold text-yellow-400">Not Enabled</h3>
                                </div>
                                <p className="text-yellow-200 mb-4 text-sm sm:text-base">Index your entities to enable oasis search capabilities.</p>
                                <button 
                                    disabled={hasActiveOasisRequests()}
                                    className={`w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 ${
                                        hasActiveOasisRequests() 
                                            ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                                            : 'bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500'
                                    }`}
                                    onClick={() => indexEntities("oasis")}
                                >
                                    Enable Oasis Search
                                </button>
                                {(hasActiveOasisRequests()) && (
                                    <p className="text-yellow-400 text-sm mt-2">
                                        Index buttons are disabled while requests are processing
                                    </p>
                                )}
                                {hasActiveOasisRequests() || hasFailedOasisRequests() ? (
                                    <div className="mt-4 space-y-3">
                                        {oasisIndexRequests.map((request) => {
                                            const statusInfo = parsePipelineStatus(request.pipeline_status);
                                            const isFailed = statusInfo.failed > 0;
                                            return (
                                                <div key={request.id} className={`flex items-center space-x-3 ${isFailed ? 'text-red-400' : 'text-purple-400'}`}>
                                                    {isFailed ? (
                                                        <div className="w-4 h-4 text-red-400">
                                                            <svg fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                                                    )}
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium">{statusInfo.statusText}</div>
                                                        {statusInfo.total > 0 && (
                                                            <div className={`text-xs space-x-2 ${isFailed ? 'text-red-300' : 'text-purple-300'}`}>
                                                                {statusInfo.pending > 0 && <span>Pending: {statusInfo.pending}</span>}
                                                                {statusInfo.running > 0 && <span>Running: {statusInfo.running}</span>}
                                                                {statusInfo.completed > 0 && <span>Completed: {statusInfo.completed}</span>}
                                                                {statusInfo.failed > 0 && <span className="text-red-400">Failed: {statusInfo.failed}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : <></>}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default GettingStarted;